import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Download, Mail, Calendar, FileText, TrendingUp, BarChart3, 
  RefreshCw, FileSpreadsheet, CheckCircle, XCircle, Clock
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const ExportHistory = () => {
  const { businessId } = useParams();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    report_type: '',
    format: [],
    delivery_method: [],
    timestamp: { min: '', max: '' }
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.exportHistory();
      setHistory(response.data.history || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch export history:', error);
      setHistory([]);
      setStats({ total_exports: 0, by_delivery: { download: 0, email: 0 }, by_format: {} });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      'sales-summary': 'Sales Summary',
      'purchase-summary': 'Purchase Summary',
      'party-outstanding': 'Party Outstanding',
      'low-stock': 'Low Stock Report',
      'cash-bank-summary': 'Cash & Bank Summary',
      'gstr1': 'GSTR-1',
      'gstr3b': 'GSTR-3B',
      'hsn-summary': 'HSN Summary',
      'sales-register': 'Sales Register',
      'purchase-register': 'Purchase Register'
    };
    return labels[type] || type?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Report';
  };

  const getModuleLabel = (type) => {
    if (type?.includes('sales')) return 'Sales';
    if (type?.includes('purchase')) return 'Purchases';
    if (type?.includes('party')) return 'Parties';
    if (type?.includes('stock')) return 'Stock';
    if (type?.includes('cash') || type?.includes('bank')) return 'Cash & Bank';
    if (type?.includes('gst') || type?.includes('hsn')) return 'GST';
    return 'Reports';
  };

  const getFormatBadge = (format) => {
    const styles = {
      pdf: { bg: 'bg-rose-50', text: 'text-rose-700', label: 'PDF' },
      excel: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Excel' },
      xlsx: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Excel' },
      json: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'JSON' },
      csv: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'CSV' }
    };
    return styles[format?.toLowerCase()] || { bg: 'bg-slate-50', text: 'text-slate-700', label: format?.toUpperCase() || 'N/A' };
  };

  // Apply column filters
  const filteredHistory = history.filter(item => {
    const matchesType = !columnFilters.report_type || 
      item.report_type?.toLowerCase().includes(columnFilters.report_type.toLowerCase());
    
    const matchesFormat = columnFilters.format.length === 0 || 
      columnFilters.format.includes(item.format?.toLowerCase());
    
    const matchesMethod = columnFilters.delivery_method.length === 0 || 
      columnFilters.delivery_method.includes(item.delivery_method);
    
    const matchesDate = (!columnFilters.timestamp?.min || new Date(item.timestamp) >= new Date(columnFilters.timestamp.min)) &&
      (!columnFilters.timestamp?.max || new Date(item.timestamp) <= new Date(columnFilters.timestamp.max));
    
    return matchesType && matchesFormat && matchesMethod && matchesDate;
  });

  const mostUsedFormat = stats.by_format && Object.keys(stats.by_format).length > 0
    ? Object.entries(stats.by_format).sort((a, b) => b[1] - a[1])[0][0].toUpperCase()
    : 'N/A';

  return (
    <div className="h-full flex flex-col" data-testid="export-history-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Export History"
        stats={[
          { icon: FileText, label: 'Total Exports', value: stats.total_exports || 0, color: 'slate' },
          { icon: Download, label: 'Downloads', value: stats.by_delivery?.download || 0, color: 'emerald' },
          { icon: Mail, label: 'Emails Sent', value: stats.by_delivery?.email || 0, color: 'purple' },
          { icon: TrendingUp, label: 'Most Used', value: mostUsedFormat, color: 'amber' },
        ]}
        ctaLabel="Refresh"
        onCtaClick={fetchHistory}
        ctaIcon={RefreshCw}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading export history...</p>
              </div>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No export history yet</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">Your export activity will appear here</p>
              <p className="text-[12px] text-slate-400 max-w-md text-center">
                Export reports from Sales, Purchases, GST, or any other module to see them logged here
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* TABLE HEADER */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '15%' }}>
                        <div className="flex items-center gap-1">
                          Date & Time
                          <ColumnFilter 
                            type="date" 
                            value={columnFilters.timestamp}
                            onChange={(val) => setColumnFilters({...columnFilters, timestamp: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '20%' }}>
                        <div className="flex items-center gap-1">
                          Report Type
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.report_type}
                            onChange={(val) => setColumnFilters({...columnFilters, report_type: val})}
                            placeholder="Filter report..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>Module</th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          Format
                          <ColumnFilter 
                            type="status" 
                            value={columnFilters.format}
                            onChange={(val) => setColumnFilters({...columnFilters, format: val})}
                            options={[
                              { value: 'pdf', label: 'PDF' },
                              { value: 'excel', label: 'Excel' },
                              { value: 'json', label: 'JSON' },
                              { value: 'csv', label: 'CSV' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Method
                          <ColumnFilter 
                            type="status" 
                            value={columnFilters.delivery_method}
                            onChange={(val) => setColumnFilters({...columnFilters, delivery_method: val})}
                            options={[
                              { value: 'download', label: 'Download' },
                              { value: 'email', label: 'Email' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Status</th>
                      <th className="tl-th tl-th-teal" style={{ width: '15%' }}>Recipients</th>
                      <th className="tl-th tl-th-teal" style={{ width: '6%' }}>Action</th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* TABLE BODY */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredHistory.map((item, index) => {
                      const formatBadge = getFormatBadge(item.format);
                      return (
                        <tr 
                          key={index} 
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        >
                          <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              {formatDate(item.timestamp)}
                            </div>
                          </td>
                          <td className="tl-td" style={{ width: '20%' }}>
                            <span className="tl-cell-primary">{getReportTypeLabel(item.report_type)}</span>
                          </td>
                          <td className="tl-td" style={{ width: '12%' }}>
                            <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[11px] font-medium">
                              {getModuleLabel(item.report_type)}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '10%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 ${formatBadge.bg} ${formatBadge.text} rounded-md text-[11px] font-medium`}>
                              <FileSpreadsheet className="w-3 h-3 mr-1" />
                              {formatBadge.label}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '12%' }}>
                            {item.delivery_method === 'email' ? (
                              <span className="inline-flex items-center gap-1.5 text-purple-600">
                                <Mail className="w-3.5 h-3.5" />
                                <span className="text-[12px] font-medium">Email</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-emerald-600">
                                <Download className="w-3.5 h-3.5" />
                                <span className="text-[12px] font-medium">Download</span>
                              </span>
                            )}
                          </td>
                          <td className="tl-td" style={{ width: '10%' }}>
                            {item.status === 'failed' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 rounded-full text-[11px] font-medium">
                                <XCircle className="w-3 h-3" />
                                Failed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Success
                              </span>
                            )}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>
                            {item.recipients && item.recipients.length > 0 ? (
                              <span className="text-[12px] text-slate-600 truncate block max-w-[150px]" title={item.recipients.join(', ')}>
                                {item.recipients.join(', ')}
                              </span>
                            ) : (
                              <span className="text-[12px] text-slate-400">—</span>
                            )}
                          </td>
                          <td className="tl-td" style={{ width: '6%' }}>
                            <div className="flex items-center gap-1">
                              {item.file_url && (
                                <button
                                  onClick={() => window.open(item.file_url, '_blank')}
                                  className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                                  title="Download"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              )}
                              {item.status === 'failed' && (
                                <button
                                  className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                                  title="Retry"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* TABLE FOOTER */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredHistory.length}</span> of <span className="font-medium text-slate-700">{history.length}</span> exports
                </p>
                <p>
                  Total exports: <span className="font-medium text-slate-700">{stats.total_exports || 0}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportHistory;
